// What's on GG Bharat: every game, show and event, newest first within each kind. This list drives the homepage's
// "stage" and the pages at /games/<id>, /shows/<id> and /events/<id>.
//
// To post a new show or event, add an entry here and deploy:
//   { id: 'diwali-s17', kind: 'event', title: 'Diwali on the Plaza', tagline: 'Lights over Sector 17',
//     status: 'announced', when: '2026-11-08T19:30:00+05:30', where: 'Chandigarh · Sector 17 Plaza',
//     image: '/site/shots/s17.webp', blurb: 'What happens, who it's for, what to bring.', host: 'GG Bharat' }
// Put its picture in public/site/ (a screenshot from inside the game is best). A kind with nothing in it shows a
// "first one soon" card, so the homepage never looks empty and never claims something that isn't there.

export type Kind = 'game' | 'show' | 'event';
export type Status = 'live' | 'beta' | 'announced' | 'soon' | 'past';

export interface Experience {
  /** in the address: /games/<id> */
  id: string;
  kind: Kind;
  title: string;
  /** one line under the title */
  tagline: string;
  status: Status;
  /** ISO date-time (IST) for shows and events */
  when?: string;
  /** the world it happens in */
  where?: string;
  /** public/… */
  image?: string;
  blurb: string;
  /** who's putting it on */
  host?: string;
}

export const EXPERIENCES: Experience[] = [
  {
    id: 'chandigarh',
    kind: 'game',
    title: 'Chandigarh',
    tagline: 'The City Beautiful',
    status: 'beta',
    where: 'Chandigarh, at 1:1',
    image: '/site/shots/aerial.webp',
    blurb:
      'Le Corbusier’s planned city, rebuilt from open map data and hundreds of photographs. Walk it, ride it, drive it, fly a drone over it, with everyone else in the same city at once.',
    host: 'GG Bharat',
  },
];

export const KINDS: { kind: Kind; label: string; plural: string; path: string; empty: string; pitch: string }[] = [
  {
    kind: 'game',
    label: 'Game',
    plural: 'Games',
    path: '/games',
    pitch: 'Open worlds of real Indian cities, built street by street.',
    empty: 'The next city is on the drawing board.',
  },
  {
    kind: 'show',
    label: 'Show',
    plural: 'Shows',
    path: '/shows',
    pitch: 'Live shows inside our worlds: music, comedy, premieres. Sit in the crowd with your friends, from your phone.',
    empty: 'The first show is being planned. Claim your name to hear first.',
  },
  {
    kind: 'event',
    label: 'Event',
    plural: 'Events',
    path: '/events',
    pitch: 'Races, treasure hunts and festivals across the city, where everyone plays at once.',
    empty: 'The first event is being planned. Claim your name to hear first.',
  },
];

export const STATUS_LABEL: Record<Status, string> = {
  live: 'Live now',
  beta: 'Closed beta',
  announced: 'Announced',
  soon: 'Coming soon',
  past: 'Happened',
};

export const byKind = (k: Kind) => EXPERIENCES.filter((e) => e.kind === k);
export const experienceAt = (path: string) => {
  const m = path.match(/^\/(games|shows|events)\/([a-z0-9-]+)\/?$/);
  if (!m) return null;
  const kind = (m[1] === 'games' ? 'game' : m[1] === 'shows' ? 'show' : 'event') as Kind;
  return EXPERIENCES.find((e) => e.kind === kind && e.id === m[2]) ?? null;
};
export const hrefOf = (e: Experience) => `${KINDS.find((k) => k.kind === e.kind)!.path}/${e.id}`;
